import { BaseRepository } from '../BaseRepository';
import { BFF_ENDPOINTS, type BffEndpoint } from '../../endpoints/BffEndpoints';
import type { SubscriptionResponse } from '../../models/bff/Subscription';
import type { SubscriptionConfigurationResponse } from '../../models/bff';
import { type CancellationEligibility, type CancellationPayload, type CancellationResponse, DEFAULT_REASON, type DeleteCancellationResponse } from '../../models/bff/Cancellation';
import { BffAuthenticatedApiClient } from '../../clients/BffAuthenticatedApiClient';
import type { HttpResponse } from '../../http/HttpClient';

const NAME = 'subscription';

/**
 * SubscriptionRepository class for handling subscription-related API operations.
 */
export class SubscriptionRepository extends BaseRepository<BffEndpoint> {
  static override readonly name = NAME;

  constructor() {
    super(BffAuthenticatedApiClient.getInstance());
  }

  /**
   * Get the logged-in user's subscription details
   *
   * @param subscriptionId - The user's subscription id
   * @returns Promise<HttpResponse<SubscriptionDto>> - The subscription of the logged in user
   */
  getSubscription(subscriptionId: string): Promise<HttpResponse<SubscriptionResponse>> {
    return this.apiClient.get<SubscriptionResponse>(
      BFF_ENDPOINTS.SUBSCRIPTION,
      {
        pathParams: {
          subscriptionId,
        },
      },
    );
  }

  cancelSubscription(subscriptionId: string, cancelType: CancellationEligibility, reason?: { code?: string, text: string }): Promise<HttpResponse<CancellationResponse>> {
    return this.apiClient.post<CancellationResponse>(
      BFF_ENDPOINTS.CANCEL_SUBSCRIPTION,
      {
        reason: reason?.code ?? DEFAULT_REASON,
        reason_text: reason?.text,
        requested_by: 'end_user',
        type: cancelType,
      } satisfies CancellationPayload,
      {
        pathParams: {
          subscriptionId,
        },
      },
    );
  }

  undoCancel(subscriptionId: string, cancellationId: string): Promise<HttpResponse<CancellationResponse>> {
    return this.apiClient.delete<DeleteCancellationResponse>(
      BFF_ENDPOINTS.SUBSCRIPTION_CANCELLATION,
      {
        pathParams: {
          subscriptionId,
          cancellationId,
        },
      },
    );
  }

  /**
   * Retrieve subscription configuration flags for the authenticated user.
   * @returns Promise<HttpResponse<SubscriptionFeaturesResponse>> - The subscription features
   */
  getFeatures(subscriptionId: string): Promise<HttpResponse<SubscriptionConfigurationResponse>> {
    return this.apiClient.get<SubscriptionConfigurationResponse>(BFF_ENDPOINTS.SUBSCRIPTION_FEATURES, { pathParams: { subscriptionId } });
  }
}

declare module '../Repositories' {
  export interface Repositories {
    [NAME]: SubscriptionRepository;
  }
}